import { Injectable } from '@angular/core';
import { DictionariesService } from 'app/services/dictionaries.service';
import { ListTransformerService } from './list-transformer.service';
import { Store } from '@ngrx/store';
import {
  BookmarksSearchFacets,
  BookmarksSearchType,
  ListItem,
  ListWithItemsCount,
} from '../models/list';
import { BookmarkSearch } from '../components/searchable-bookmarks-search-bar/searchable-bookmarks-search-bar.component';
import {
  AvailabilityStatus,
  FormatGroup,
  MaterialTab,
} from 'app/entity/models/entity';
import {
  FacetResult,
  ResponseFacet,
} from 'search/facets/models/resource-facet';
import {
  AvailabilityStatusToFilterMap,
  GRAY_FILTER,
  MATERIAL_ICONS,
} from 'app/models/material-icons';
import { DictionaryItem, DictionaryTypes } from 'app/models/dictionaries';
import { ListItemWithIcons } from '../components/searchable-bookmarks-results-preview/searchable-bookmarks-results-preview.component';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  forkJoin,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { getLists, isLastPageLoaded } from '../reducers/list.reducer';
import { ListItemsDtoWithoutSortBy, ListService } from './list.service';
import { FacetSearchObject } from 'search/models/search-object';
import { ResourceFacetBlock } from 'search/models/filter-panel';
import { FacetsService } from 'search/facets/services/facets.service';

type Icon = {
  url: string;
  filter: string;
  label: string;
  formatGroup?: string;
  iconCrossline?: string;
};

@Injectable()
export class ListSearchService {
  public lists$: BehaviorSubject<ListWithItemsCount[]> = new BehaviorSubject(
    null
  );
  public facets$: BehaviorSubject<BookmarksSearchFacets> = new BehaviorSubject(
    null
  );
  public initialized: boolean = false;
  public listsSearch: ListWithItemsCount[] = [];
  public searchPreview: ListItem[] = [];
  public available: ListItem[] = [];
  public initialCount: number = 0;
  public initialAvailableCount: number = 0;
  public count: number = 0;
  public availableCount: number = 0;

  constructor(
    private readonly listService: ListService,
    private readonly dictionariesService: DictionariesService,
    private readonly listTransformerService: ListTransformerService,
    private readonly store: Store,
    private facetsService: FacetsService
  ) {}

  public markMatches(
    list: ListWithItemsCount,
    query: BookmarkSearch,
    facets: BookmarksSearchFacets,
    initial = false
  ) {
    const regEx = '/[^A-Z0-9]/ig';
    const text = query.text.toLowerCase().replace(regEx, '');
    const type = query.type ?? BookmarksSearchType.Author;
    let matchingCodes: string[] = [];

    if (text) {
      list.items.forEach((item) => {
        item.matched = false;
        if (initial) {
          item.selected = false;
        }
        if (this.isAvailable(item)) {
          item.available = true;
        } else {
          item.available = false;
        }

        const fg = item.entity.sourceEntity as FormatGroup;
        const materialTabs = fg.materialTabs ?? [];
        switch (type) {
          case BookmarksSearchType.Format:
            matchingCodes = this.findMatchingIds(text, facets.format);
            for (const m of materialTabs) {
              const materialTypes = m.materialTypes ?? [];
              for (const t of materialTypes) {
                if (matchingCodes.indexOf(t) > -1) {
                  item.matched = true;
                  break;
                }
              }
            }
            break;
          case BookmarksSearchType.Language:
            matchingCodes = this.findMatchingIds(text, facets.language);
            if (matchingCodes.indexOf(fg.language) > -1) {
              item.matched = true;
              break;
            }
            break;
          case BookmarksSearchType.Location:
            matchingCodes = this.findMatchingIds(text, facets.language);
            for (const m of materialTabs) {
              const locations = m.locations ?? [];
              for (const l of locations) {
                if (matchingCodes.indexOf(l.code)) {
                  item.matched = true;
                  break;
                }
              }
            }
            break;
          case BookmarksSearchType.Title:
            if (fg.title.toLowerCase().replace(regEx, '').includes(text)) {
              item.matched = true;
              break;
            }
            break;
          case BookmarksSearchType.Author:
            const normalize = (str: string): string =>
              (str || '').toLowerCase().replace(/[^a-z]/g, '');
            const inputText = (query.text || '')
              .toLowerCase()
              .replace(/[^a-z\s]/g, ' ');
            const inputParts = inputText.split(/\s+/).filter(Boolean);
            const normalizedLabel = normalize(fg.primaryAgent?.label || '');
            const allPartsMatch = inputParts.every((part) =>
              normalizedLabel.includes(part)
            );
            if (allPartsMatch) {
              item.matched = true;
            }
            break;
          case BookmarksSearchType.DateFrom:
            if (parseInt(fg.publicationDate)) {
              const dates = fg.publicationDate.split('-');
              for (const d of dates) {
                if (parseInt(d) === parseInt(text)) {
                  item.matched = true;
                  break;
                }
              }
            }
            break;
          case BookmarksSearchType.Series:
            if (
              fg.seriesTitle?.toLowerCase().replace(regEx, '').includes(text) ||
              fg.seriesTitleSearch
                ?.toLowerCase()
                .replace(regEx, '')
                .includes(text)
            ) {
              item.matched = true;
              break;
            }
            break;
          case BookmarksSearchType.Concept:
            if (fg.title.toLowerCase().replace(regEx, '').includes(text)) {
              item.matched = true;
              break;
            }
            break;
        }
        if (query.availableOnly && !item.available) {
          item.matched = false;
        }
      });
    } else {
      list.items.forEach((item) => {
        item.matched = true;
        if (this.isAvailable(item)) {
          item.available = true;
        } else {
          item.available = false;
        }
        if (query.availableOnly && !item.available) {
          item.matched = false;
        }
      });
    }
  }

  private findMatchingIds(
    searchText: string,
    facet: FacetResult<ResponseFacet>
  ) {
    const regEx = '/[^A-Z0-9]/ig';
    const text = searchText.toLowerCase().replace(regEx, '');
    const ids = facet.data
      .filter((value) =>
        value.label.toLowerCase().replace(regEx, '').includes(text)
      )
      .map((value) => value.id);
    return ids;
  }

  private getIcons(materialTabs: MaterialTab[]) {
    const icons: Icon[] = [];
    materialTabs?.slice(0, 4).forEach((m, index) => {
      const code = m.materialTypes?.[0];
      const item: DictionaryItem =
        this.dictionariesService.getDictionaryItemByCode(
          DictionaryTypes.MATERIAL_TYPES,
          code
        );
      const iconKey =
        materialTabs.length > 4 && index === 3
          ? 'default-other'
          : item?.icon ?? 'default-other';

      const iconDetails = MATERIAL_ICONS[iconKey as string];
      const iconCrossline = MATERIAL_ICONS['crossline'];
      const url = iconDetails?.icon || '';
      const label = iconDetails?.label || '';
      const formatGroup = item?.formatGroup;
      const status = m.availability.status.general;
      const isCheckout = status === 'CheckedOut' || status === 'Unavailable';

      const availabilityStatus =
        m.availability.status.general ?? AvailabilityStatus.UNAVAILABLE;

      const filter = iconKey === 'default-other' ? GRAY_FILTER : AvailabilityStatusToFilterMap[availabilityStatus];
      icons.push({
        url,
        filter,
        label,
        formatGroup,
        iconCrossline: isCheckout ? iconCrossline.icon : '',
      });
    });
    return icons;
  }

  private isAvailable(item: ListItem) {
    const validStatuses = [
      AvailabilityStatus.AVAILABLE,
      AvailabilityStatus.CHECK_AVAILABILITY,
    ];
    const source = item?.entity?.sourceEntity;
    const materialTabs = (source as FormatGroup)?.materialTabs;
    if (!materialTabs) {
      return false;
    }
    for (const tab of materialTabs) {
      const generalStatus = tab.availability?.status?.general;
      if (validStatuses.includes(generalStatus)) {
        return true;
      }
    }
    return false;
  }

  public generateSearchPreview(
    combined: ListItem[],
    checkAvailability = true
  ): ListItem[] {
    const prefered = 6;
    const max = combined.length > prefered ? prefered : combined.length;
    const preview = [];

    for (let i = combined.length - 1; i >= 0; i--) {
      if (preview.length >= max) {
        break;
      }
      const j = Math.floor(Math.random() * (i + 1));
      [combined[i], combined[j]] = [combined[j], combined[i]];
      const candidate = combined[i];
      if (!checkAvailability || this.isAvailable(candidate)) {
        preview.push(candidate);
      }
    }
    return preview.map((item) => {
      (item as ListItemWithIcons).icons = this.getIcons(
        (item.entity.sourceEntity as FormatGroup).materialTabs
      );
      return item;
    });
  }

  public async loadAllSequentially() {
    this.getFacets().subscribe((facets) => {
      const [format, location, language] = facets;
      const searchFacets: BookmarksSearchFacets = {
        format,
        location,
        language,
      };
      this.facets$.next(searchFacets);
    });
    this.store.select(getLists).subscribe(async (ls) => {
      if (this.initialized) {
        return;
      }
      this.initialized = true;
      const listsImmutable = JSON.parse(JSON.stringify(ls));
      const lists: ListWithItemsCount[] = [];
      for (const list of listsImmutable) {
        let nextPage = list.pagination.page + 1;
        const totalPages = list.pagination.totalPages;
        const PAGE_SIZE = 50;
        while (!isLastPageLoaded(list)) {
          // load all items
          const res$ = this.listService
            .getItems(list.id, 'date.asc', {
              pageNum: nextPage,
              pageSize: PAGE_SIZE,
            })
            .pipe(
              catchError(() => {
                return of({
                  data: [],
                  page: nextPage,
                  totalPages,
                } as ListItemsDtoWithoutSortBy);
              })
            );
          const res = await firstValueFrom(res$);
          const entities = res.data.map((item) =>
            this.listTransformerService.transformFgOrEntityToListItemEntity(
              item
            )
          );
          const items: ListItem[] = entities.map((e) => {
            return {
              id: e.id,
              matched: false,
              selected: false,
              entity: e,
            };
          });
          list.items.push(...items);
          list.pagination.page = nextPage;
          nextPage++;
        }
        lists.push(list);
      }
      this.lists$.next(lists);
    });
  }

  public searchAll(
    search: BookmarkSearch,
    initial = false
  ) {
    if (!this.initialized) {
      this.loadAllSequentially();
    }
    return combineLatest([
      this.lists$,
      this.facets$,
    ]).pipe(
      filter(([lists, facets]) => !!lists && !!facets),
      switchMap(([lists, facets]) => {
        this.listsSearch = [];
        for (const list of lists) {
          // mark matches
          this.markMatches(list, search, facets, initial);
          const openedListSearch = {
            ...list,
            items: list.items.filter((i) => i.matched),
          };
          openedListSearch.itemsCount = openedListSearch.items.length;
          this.listsSearch.push(openedListSearch);
        }
        const items = lists.flatMap((l) => l.items);
        this.count = items.filter((i) => i.matched).length;
        this.available = items.filter((i) => i.available);
        this.availableCount = this.available.filter((i) => i.matched).length;
        this.searchPreview = this.generateSearchPreview(
          this.available.filter((i) => i.matched),
          false
        );
        if (initial) {
          this.initialCount = this.count;
          this.initialAvailableCount = this.availableCount;
        }
        return of({
          listsSearch: this.listsSearch,
          searchPreview: this.searchPreview,
          available: this.available,
          initialCount: this.initialCount,
          initialAvailableCount: this.initialAvailableCount,
          count: this.count,
          availableCount: this.availableCount,
        });
      })
    );
  }

  public getFacets(): Observable<FacetResult<ResponseFacet>[]> {
    return forkJoin([
      this.getFormats(),
      this.getLocations(),
      this.getLanguages(),
    ]);
  }

  public getFormats(): Observable<FacetResult<ResponseFacet>> {
    const query: FacetSearchObject = {
      facetKey: ResourceFacetBlock.FORMATS,
      facetMapField: 'MaterialType',
    };
    const options = {
      pageNum: 0,
      pageSize: 100,
    };
    return this.facetsService.getSingleFacet(query, options);
  }

  public getLocations(): Observable<FacetResult<ResponseFacet>> {
    const query: FacetSearchObject = {
      facetKey: ResourceFacetBlock.LOCATION,
      facetMapField: 'Location',
    };
    const options = {
      pageNum: 0,
      pageSize: 100,
    };
    return this.facetsService.getSingleFacet(query, options);
  }

  public getLanguages(): Observable<FacetResult<ResponseFacet>> {
    const query: FacetSearchObject = {
      facetKey: ResourceFacetBlock.LANGUAGE,
      facetMapField: 'Language',
    };
    const options = {
      pageNum: 0,
      pageSize: 100,
    };
    return this.facetsService.getSingleFacet(query, options);
  }
}
